/* The profile form's data rules, kept pure so they're unit-tested.
   Sensitive details (race or ethnicity, religion, family income) each have their own consent: a detail is only sent
   while its box is ticked, and unticking sends it empty so the server deletes it. Minors never send them. */
import type { AcademicRecord, FamilyMember, Profile, ProfileBundle } from '@pathabroad/core/browser';

export const SENSITIVE = ['race', 'religion', 'family_income'] as const;
export type SensitiveKey = typeof SENSITIVE[number];

/* rows keep a local key for React; it's removed before saving */
export type Keyed<T> = T & { _k?: string };
export type Draft = { profile: Profile; family: Keyed<FamilyMember>[]; academics: Keyed<AcademicRecord>[] };

let seq = 0;
export const newKey = () => `n${++seq}`;

export function toDraft(b: Partial<ProfileBundle> | null | undefined): Draft {
  const src = (b?.profile ?? {}) as Profile & { sensitive_opt_in?: boolean };
  const { sensitive_opt_in: _legacy, ...rest } = src;
  const profile: Profile = {
    ...rest,
    address: { ...(src.address ?? {}) },
    english: { ...(src.english ?? {}) },
    citizenship: Array.isArray(src.citizenship) ? src.citizenship.filter(Boolean).slice(0, 2) : [],
    sensitive_consents: { ...(src.sensitive_consents ?? {}) },
  };
  return {
    profile,
    family: (Array.isArray(b?.family) ? b!.family : []).map(m => ({ ...m, _k: m.id ?? newKey() })),
    academics: (Array.isArray(b?.academics) ? b!.academics : []).map(a => ({ ...a, school_name: a.school_name ?? '', _k: a.id ?? newKey() })),
  };
}

const trimmed = <T extends Record<string, unknown>>(o: T): T => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) out[k] = typeof v === 'string' ? v.trim() : v;
  return out as T;
};
const strip = <T extends { _k?: string }>({ _k, ...rest }: T) => rest;
const hasText = (...v: Array<string | undefined>) => v.some(x => (x ?? '').trim() !== '');

/* schools with details but no name can't be saved: returns their row indexes */
export function schoolsMissingName(d: Draft): number[] {
  return d.academics.map((a, i) => (!a.school_name.trim() && hasText(a.start_date, a.end_date, a.test_type, a.test_score) ? i : -1)).filter(i => i >= 0);
}

/* the full bundle sent to PUT /api/profile (always the whole thing, so nothing depends on how the server merges) */
export function buildPayload(d: Draft, minor: boolean): ProfileBundle {
  const profile: Profile = trimmed({ ...d.profile });
  profile.address = trimmed({ ...(d.profile.address ?? {}) });
  profile.english = trimmed({ ...(d.profile.english ?? {}) });
  profile.citizenship = [...new Set((d.profile.citizenship ?? []).filter(Boolean).map(c => c.toUpperCase()))].slice(0, 2);
  if (minor) {
    delete profile.sensitive_consents;
    for (const k of SENSITIVE) delete profile[k];
  } else {
    const c = d.profile.sensitive_consents ?? {};
    profile.sensitive_consents = { race: !!c.race, religion: !!c.religion, family_income: !!c.family_income };
    for (const k of SENSITIVE) if (!profile.sensitive_consents[k]) profile[k] = '';
  }
  delete (profile as Record<string, unknown>).sensitive_opt_in;
  return {
    profile,
    family: d.family.map(strip).map(m => trimmed(m)).filter(m => hasText(m.name, m.relation, m.education, m.occupation)),
    academics: d.academics.map(strip).map(a => trimmed(a)).filter(a => a.school_name !== ''),
  };
}

/* the next intakes students usually aim for: January, May and September for the next three years */
export function startTerms(now: Date = new Date()): Array<{ value: string; year: number; month: number }> {
  const out: Array<{ value: string; year: number; month: number }> = [];
  const cur = now.getFullYear() * 12 + now.getMonth();
  for (let y = now.getFullYear(); y <= now.getFullYear() + 3; y++) {
    for (const m of [1, 5, 9]) if (y * 12 + (m - 1) > cur) out.push({ value: `${y}-${String(m).padStart(2, '0')}`, year: y, month: m });
  }
  return out;
}
